import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AnimateSpin } from '@/components/AnimateSpin';
import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { env } from '@/config/env';
import { useAuth } from '@/contexts/AuthContext';
import { useUploadLogo } from '@/hooks/useUploadLogo';
import i18n from '@/lib/i18n';
import { CustomerLogoStatus } from '@/services/AuthService';

const formSchema = z.object({
  file: z
    .instanceof(File, { message: 'Logo is required' })
    .refine((f) => ['image/jpeg', 'image/png'].includes(f.type), {
      message: i18n.t(
        'pages.Settings.components.General.components.LogoForm.validation.fileType',
      ),
    })
    .refine((f) => f.size <= 2 * 1024 * 1024, {
      message: i18n.t(
        'pages.Settings.components.General.components.LogoForm.validation.fileSize',
      ),
    }),
});

type FormData = z.infer<typeof formSchema>;

export function LogoForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.General.components.LogoForm',
  });

  const { user } = useAuth();

  const logoObjectKey = user?.customer.logoObjectKey;
  const isPending = user?.customer.logoStatus === CustomerLogoStatus.PENDING;
  const isError = user?.customer.logoStatus === CustomerLogoStatus.ERROR;
  const isUploading =
    user?.customer.logoStatus === CustomerLogoStatus.UPLOADING;
  const isUploaded = user?.customer.logoStatus === CustomerLogoStatus.UPLOADED;

  const [isEditable, setIsEditable] = useState(() => isPending);

  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    control,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const { uploadLogo } = useUploadLogo();

  const onSubmit = handleSubmit(async ({ file }) => {
    try {
      const { url } = await uploadLogo({
        filename: file.name,
        contentType: file.type,
      });

      await axios.put(url, file, {
        headers: {
          'content-type': file.type,
          'x-amz-acl': 'public-read',
        },
      });

      toast.success(t('toast.success'));
      setIsEditable(false);
      reset();
    } catch {
      toast.error(t('toast.error'));
    }
  });

  return (
    <div className="space-y-2">
      <div className="mb-4 space-y-1.5">
        <h3 className="font-semibold leading-none tracking-tight">
          {t('title')}
        </h3>

        <p className="text-sm text-muted-foreground">{t('description')}</p>
      </div>

      {(isPending || isEditable) && (
        <form className="space-y-2" onSubmit={onSubmit}>
          <FormGroup
            className="space-y-2"
            error={{ message: errors.file?.message, font: 'sm' }}
          >
            <Controller
              name="file"
              control={control}
              render={({ field: { onChange, ref } }) => (
                <Input
                  type="file"
                  disabled={!isEditable}
                  className="max-w-[280px]"
                  onChange={(e) =>
                    onChange((e.target as HTMLInputElement).files?.[0] ?? null)
                  }
                  ref={ref}
                />
              )}
            />
          </FormGroup>

          <div className="space-x-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('savingButton') : t('saveButton')}
            </Button>

            {isUploaded && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditable(false)}
              >
                {t('cancelButton')}
              </Button>
            )}
          </div>
        </form>
      )}

      {!isPending && !isEditable && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            {isUploading && <AnimateSpin />}

            {isError && (
              <p className="text-sm text-red-400">{t('errorMessage')}</p>
            )}

            {isUploaded && logoObjectKey && (
              <img
                src={`${env.BUCKET_URL}/${logoObjectKey}`}
                alt="Logo"
                className="max-h-[192px] max-w-[192px]"
              />
            )}
          </div>

          {!isUploading && (
            <Button size="sm" onClick={() => setIsEditable(true)}>
              {t('editButton')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
